/**
 * Layer 4: RolePersona
 *
 * Controls WHO evaluates WHOM - power dynamics, role-to-candidate fit, and transparency scoring.
 *
 * This layer enables the "Power Reversal" feature where candidates evaluate organizations.
 */

export type RolePersona =
  | 'candidate'        // Job seeker evaluating opportunities
  | 'hiring-manager'   // Manager seeking candidates for open roles
  | 'evaluator'        // Technical interviewer assessing candidates
  | 'peer'             // Fellow engineer reviewing for culture fit

export const ROLE_PERSONAS: RolePersona[] = [
  'candidate',
  'hiring-manager',
  'evaluator',
  'peer',
]

export interface EvaluationCriteria {
  transparencyImportance: 'low' | 'medium' | 'high'
  cultureAlignmentImportance: 'low' | 'medium' | 'high'
  compensationFairnessImportance: 'low' | 'medium' | 'high'
  growthOpportunityImportance: 'low' | 'medium' | 'high'
  workLifeBalanceImportance: 'low' | 'medium' | 'high'
}

export interface PowerDynamics {
  canEvaluateOrganizations: boolean
  canRequestCompensationData: boolean
  canAccessTransparencyScores: boolean
  canViewCandidateEvaluations: boolean
  canPostRoles: boolean
  canReviewCandidates: boolean
}

export interface RolePersonaConfig {
  id: RolePersona
  name: string
  description: string
  perspective: 'seeking-opportunity' | 'offering-opportunity' | 'peer-review'
  evaluationCriteria: EvaluationCriteria
  powerDynamics: PowerDynamics
  visibleFeatures: string[]
  hiddenFeatures: string[]
}

export const ROLE_PERSONA_CONFIGS: Record<RolePersona, RolePersonaConfig> = {
  candidate: {
    id: 'candidate',
    name: 'Candidate',
    description: 'Job seeker evaluating opportunities and organizations',
    perspective: 'seeking-opportunity',
    evaluationCriteria: {
      transparencyImportance: 'high',
      cultureAlignmentImportance: 'high',
      compensationFairnessImportance: 'high',
      growthOpportunityImportance: 'high',
      workLifeBalanceImportance: 'high',
    },
    powerDynamics: {
      canEvaluateOrganizations: true,
      canRequestCompensationData: true,
      canAccessTransparencyScores: true,
      canViewCandidateEvaluations: true,
      canPostRoles: false,
      canReviewCandidates: false,
    },
    visibleFeatures: [
      'organization-search',
      'transparency-scores',
      'compensation-ranges',
      'candidate-reviews',
      'interview-experiences',
      'culture-ratings',
      'submit-evaluation',
    ],
    hiddenFeatures: ['post-role', 'review-candidates', 'candidate-pipeline'],
  },
  'hiring-manager': {
    id: 'hiring-manager',
    name: 'Hiring Manager',
    description: 'Manager seeking candidates for open roles',
    perspective: 'offering-opportunity',
    evaluationCriteria: {
      transparencyImportance: 'medium',
      cultureAlignmentImportance: 'high',
      compensationFairnessImportance: 'medium',
      growthOpportunityImportance: 'low',
      workLifeBalanceImportance: 'low',
    },
    powerDynamics: {
      canEvaluateOrganizations: false,
      canRequestCompensationData: false,
      canAccessTransparencyScores: true,
      canViewCandidateEvaluations: true,
      canPostRoles: true,
      canReviewCandidates: true,
    },
    visibleFeatures: [
      'post-role',
      'candidate-search',
      'review-candidates',
      'candidate-pipeline',
      'organization-profile',
      'transparency-dashboard',
      'candidate-feedback',
    ],
    hiddenFeatures: ['submit-candidate-evaluation', 'rate-interview-experience'],
  },
  evaluator: {
    id: 'evaluator',
    name: 'Technical Evaluator',
    description: 'Technical interviewer assessing candidates',
    perspective: 'peer-review',
    evaluationCriteria: {
      transparencyImportance: 'low',
      cultureAlignmentImportance: 'medium',
      compensationFairnessImportance: 'low',
      growthOpportunityImportance: 'low',
      workLifeBalanceImportance: 'low',
    },
    powerDynamics: {
      canEvaluateOrganizations: false,
      canRequestCompensationData: false,
      canAccessTransparencyScores: false,
      canViewCandidateEvaluations: false,
      canPostRoles: false,
      canReviewCandidates: true,
    },
    visibleFeatures: [
      'candidate-search',
      'review-candidates',
      'technical-assessment',
      'interview-feedback',
    ],
    hiddenFeatures: [
      'post-role',
      'transparency-scores',
      'compensation-ranges',
      'candidate-evaluations',
    ],
  },
  peer: {
    id: 'peer',
    name: 'Peer Reviewer',
    description: 'Fellow engineer reviewing for culture fit',
    perspective: 'peer-review',
    evaluationCriteria: {
      transparencyImportance: 'medium',
      cultureAlignmentImportance: 'high',
      compensationFairnessImportance: 'low',
      growthOpportunityImportance: 'medium',
      workLifeBalanceImportance: 'medium',
    },
    powerDynamics: {
      canEvaluateOrganizations: true,
      canRequestCompensationData: false,
      canAccessTransparencyScores: true,
      canViewCandidateEvaluations: true,
      canPostRoles: false,
      canReviewCandidates: true,
    },
    visibleFeatures: [
      'candidate-search',
      'review-candidates',
      'culture-assessment',
      'peer-feedback',
      'transparency-scores',
      'candidate-reviews',
    ],
    hiddenFeatures: ['post-role', 'compensation-ranges', 'candidate-pipeline'],
  },
}
